import { getFragmentData } from '@packages/gql/generated/shopping';
import { CriteoProductsDocument } from '@packages/gql/generated/shopping/CriteoProductsDocument';
import { ProductRecommendationFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductRecommendationFragmentFragmentDoc';
import { type CriteoPageId } from '@packages/gql/generated/shopping/graphql';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { ProductsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel';
import { RecoCarouselSkeleton } from '../../RecoCarouselSkeleton';
import { RecoProductCard } from '../../RecoProductCard';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';

export type CriteoProductsRecoSliderProps = Pick<
  RequestParams,
  'available' | 'brand' | 'productId' | 'price'
> &
  Omit<ProductsRecoSliderProps, 'preferTitle'>;

/* GraphQL */ `
  query CriteoProducts(
    $available: Int!
    $pageId: CriteoPageId!
    $price: Float!
    $productId: String!
    $brand: String!
  ) {
    criteoViewItem(
      params: {
        productId: $productId
        available: $available
        pageId: $pageId
        price: $price
        brand: $brand
      }
    ) {
      type
      recommendations {
        ...ProductRecommendationFragment
      }
      ...CriteoPlacementBeaconsFragment
    }
  }
`;

const RDE_TYPE: RdeType = 'criteoViewItem';

/**
 * CriteoProductsRecoSlider component description displayed in storybook
 * */
export const CriteoProductsRecoSlider: FC<CriteoProductsRecoSliderProps> = ({
  available,
  brand,
  elementIndex = 0,
  fetchOnThreshold = false,
  onProductClick,
  onSeen,
  pageTemplate,
  productId,
  price,
  title,
}) => {
  const { isMobile } = useDeviceType();
  const { isInThreshold, isInView, recoEl } = useRecoElement();
  const hasCookieConsent = useCookieConsent('C0004');

  const { getCookies } = useCookies();
  const { appView } = getCookies();
  const isApp = appView === 'true';

  const pageId: CriteoPageId = (() => {
    if (isApp) return 'App';
    if (isMobile) return 'Mobile';
    return 'Desktop';
  })();

  const [{ data, error }] = useQuery({
    query: CriteoProductsDocument,
    variables: {
      available,
      productId,
      pageId,
      price,
      brand,
    },
    context: useMemo(() => ({ suspense: false }), []),
    pause: hasCookieConsent && (fetchOnThreshold ? !isInThreshold : false),
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.criteoViewItem.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) =>
          getFragmentData(ProductRecommendationFragmentFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.criteoViewItem.type;

  useProductsRecoTracking({
    elementIndex,
    pageTemplate,
    placementBeaconsData: data?.criteoViewItem,
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title,
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
    onSeen,
  });

  if (
    !hasCookieConsent ||
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  return (
    <div ref={recoEl}>
      {!filteredRecommendations ? (
        <RecoCarouselSkeleton />
      ) : (
        <RecoCarousel title={title}>
          {filteredRecommendations.map((recommendation, idx) => (
            <RecoProductCard
              pageTemplate={pageTemplate}
              // rule is disabled because order doesn't change between rerenders
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              elementIndex={elementIndex}
              position={idx}
              recommendationData={recommendation}
              productsLength={filteredRecommendations.length}
              rdeType={RDE_TYPE}
              onClick={(akl, sku) => {
                onProductClick?.(akl, sku);
              }}
            />
          ))}
        </RecoCarousel>
      )}
    </div>
  );
};
